import * as React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  AppBar, Box, Toolbar, IconButton, Typography, Menu, MenuItem, Avatar,
  Badge, Button, Tooltip, Drawer, List, ListItem, ListItemButton, ListItemText, Divider, ListItemIcon
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ShareIcon from '@mui/icons-material/Share';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import PatternIcon from '@mui/icons-material/Pattern';
import FolderIcon from '@mui/icons-material/Folder';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DevicesIcon from '@mui/icons-material/Devices';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PeopleIcon from '@mui/icons-material/People';
import HomeIcon from '@mui/icons-material/Home';
import LogoutIcon from '@mui/icons-material/Logout';
import BuildIcon from '@mui/icons-material/Build';
import StorageIcon from '@mui/icons-material/Storage';
import TuneIcon from '@mui/icons-material/Tune';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import gmLogo from '../../img/logo.svg';
import slackLogo from '../../img/slack.png';
import DownloadCartDialog from '../common/Dialogs/DownloadCart/DownloadCartDialog';
import ReleaseNotesDialog from '../common/Dialogs/ReleaseNotes/ReleaseNotesDialog';
import { useAPI } from '../Hooks/useAPIs';
import { useNotification } from '../common/NotificationContext';
import { releaseNotes } from '../utils/CommonUtils';
import { routeRoleConfig } from '../common/Route/routeRoleConfig';
import { lightTheme, darkTheme } from '../../ThemeSwitcher/ThemeProvider';
import './Navbar.css';

const pages = [
  { name: 'Timeline', path: '/timeline' },
  { name: 'Files', path: '/files' },
  { name: 'DIT', path: '/dit' },
  { name: 'Upload', path: '/upload' },
  { name: 'Users', path: '/users' },
  { name: 'Devices', path: '/devices' },
  { name: 'Combiner', path: '/dbcombiner' },
  { name: 'Tools', path: '/tools' },
];

const dataBoardPages = [
  { name: 'DTC Log', path: '/dtc_log' },
  { name: 'Part Number Pull', path: '/part_number_pull' },
  { name: 'Test Result Ingestion', path: '/test_result_ingestion' },
  { name: 'Event', path: '/event' },
  { name: 'Vehicle Lookup', path: '/vehicle_lookup' },
];

const devicePages = [
  { name: 'My Devices', path: '/devices/user' },
  { name: 'All Devices', path: '/devices/admin' },
];

const navItems = [
  { type: 'page', name: 'Timeline', path: '/timeline', icon: <PatternIcon /> },
  { type: 'page', name: 'Files', path: '/files', icon: <FolderIcon /> },
  { type: 'dropdown', name: 'DataBoard', items: dataBoardPages, icon: <DashboardIcon /> },
  { type: 'dropdown', name: 'Devices', items: devicePages, icon: <DevicesIcon /> },
  { type: 'page', name: 'DIT', path: '/dit', icon: <AssignmentIcon /> },
  { type: 'page', name: 'Upload', path: '/upload', icon: <CloudUploadIcon /> },
  { type: 'page', name: 'Users', path: '/users', icon: <PeopleIcon /> },
  { type: 'page', name: 'Combiner', path: '/dbcombiner', icon: <StorageIcon /> },
  { type: 'page', name: 'Tools', path: '/tools', icon: <BuildIcon /> },
];

function Navbar({ msInstance, switchTheme, activeTheme }) {
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [badgeContent, setBadgeContent] = useState(0);
  
  const [dialogs, setDialogs] = useState({
    download: false,
    releaseNotes: false,
  });

  const isDark = activeTheme.palette.mode === 'dark';

  const handleThemeToggle = () => {
    switchTheme(isDark ? lightTheme : darkTheme);
  };

  const { apiCall } = useAPI();
  const navigate = useNavigate();
  const location = useLocation();
  const { accounts } = useMsal();

  const userRoles = React.useMemo(() => {
    if (accounts.length > 0) {
      const tokenRoles = accounts[0]?.idTokenClaims?.roles || [];
      return tokenRoles.map((role) => role.toLowerCase());
    }
    return [];
  }, [accounts]);

  const toggleDialog = React.useCallback((name, open) => {
    setDialogs((prev) => ({ ...prev, [name]: open }));
  }, []);

  const notify = useNotification();

  useEffect(() => {
    const seenReleaseNotes = localStorage.getItem('seenReleaseNotes');
    if (seenReleaseNotes !== releaseNotes.version) {
      toggleDialog('releaseNotes', true);
    }
  }, [toggleDialog]);

  const handleCloseReleaseNotes = () => {
    toggleDialog('releaseNotes', false);
    localStorage.setItem('seenReleaseNotes', releaseNotes.version);
  };

  const handlePreferencesClick = () => {
    setAnchorElUser(null);
    navigate('/preferences');
    setMobileMenuOpen(false);
  };

  const activeButton = React.useMemo(() => {
    const currentPath = location.pathname;

    const mainPage = pages.find((p) => p.path === currentPath);
    if (mainPage) return mainPage.name;

    const inDataBoard = dataBoardPages.some((p) => p.path === currentPath);
    if (inDataBoard) return 'DataBoard';

    const inDevices = devicePages.some((p) => p.path === currentPath) || currentPath === '/devices' || currentPath.startsWith('/devices/');
    if (inDevices) return 'Devices';

    if (currentPath === '/dbcombiner_task_status') return 'Combiner';

    if (currentPath === '/home' || currentPath === '/') return 'Home';
    
    return '';
  }, [location.pathname]);

  useEffect(() => {
    const handleBadgeUpdate = (event) => {
      setBadgeContent(event.detail);
    };

    window.addEventListener('updateBadgeContent', handleBadgeUpdate);

    return () => {
      window.removeEventListener('updateBadgeContent', handleBadgeUpdate);
    };
  }, []);

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(window.location.href)
      .then(() => notify({ message: 'Shareable URL copied to clipboard!', severity: 'success' }))
      .catch(() => notify({ message: 'Failed to copy URL', severity: 'error' }));
  };

  const handleSlackClick = () => {
    window.open('https://gm.enterprise.slack.com/archives/C09807PFEQ2', '_blank');
  };

  const handleLogout = () => {
    localStorage.removeItem('userDetails');
    msInstance.logoutRedirect().catch((error) => console.log(error));
  };

  const handleNavClick = (pageName, path) => {
    navigate(path);
  };

  const handleDropdownClick = (name, event) => {
    setActiveDropdown({ name, anchorEl: event.currentTarget });
  };

  const handleDropdownClose = () => {
    setActiveDropdown(null);
  };

  const handleDropdownNavClick = (dropdownName, pageName, path) => {
    navigate(path);
    handleDropdownClose();
    setMobileMenuOpen(false);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileNavClick = (pageName, path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const getUserName = () => 'SDL User';

  const hasAccessToPath = React.useCallback((path) => {
    const allowedRoles = routeRoleConfig[path] || [];
    if (allowedRoles.length === 0) return true;
    return allowedRoles
      .map((role) => role.toLowerCase())
      .some((role) => userRoles.includes(role));
  }, [userRoles]);

  return (
    <Box className="navbar-container">
      <AppBar
        className="appbar"
        position="static"
        elevation={0}
        sx={{
          bgcolor: activeTheme.palette.background.paper,
          color: activeTheme.palette.text.primary,
          borderBottom: `1px solid ${activeTheme.palette.divider}`,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        }}
      >
        <Toolbar className="toolbar" sx={{ justifyContent: 'space-between' }}>
          {/* Left Section: Mobile Menu Button, Logo, and SDL */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '0 0 auto' }}>
            {/* Mobile Menu Button */}
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={handleMobileMenuToggle}
              sx={{ 
                display: { xs: 'flex', md: 'none' }, 
                mr: 1,
                '&:hover': {
                  backgroundColor: activeTheme.palette.action.hover,
                },
              }}
            >
              <MenuIcon sx={{ fontSize: '22px' }} />
            </IconButton>

            <img src={gmLogo} alt="SDL" className="logo" />
            <Button
              onClick={() => {
                navigate('/home');
                setMobileMenuOpen(false);
              }}
              sx={{
                color: activeTheme.palette.text.primary,
                fontSize: { xs: '0.85rem', md: '1rem' },
                fontWeight: 500,
                textTransform: 'none',
                letterSpacing: '0.05rem',
                marginRight: { xs: 0.5, md: 1.5 },
                display: { xs: 'flex', md: 'flex' },
                minWidth: 'auto',
                padding: '0 4px',
                '&:hover': {
                  backgroundColor: activeTheme.palette.action.hover,
                },
              }}
            >
              SDL
            </Button>
          </Box>

          {/* Center Section: Navigation Items */}
          <Box className="nav-container" sx={{ 
            display: { xs: 'none', md: 'flex' }, 
            overflowX: 'auto',
            flex: '1 1 auto',
            justifyContent: 'center',
          }}>
            {navItems.map((item) => {
              if (item.type === 'page') {
                if (!hasAccessToPath(item.path)) return null;

                return (
                  <Button
                    key={item.name}
                    onClick={() => handleNavClick(item.name, item.path)}
                    className="nav-button"
                    startIcon={item.icon}
                    sx={{
                      color: activeTheme.palette.text.primary,
                      backgroundColor: 'transparent',
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                      '&::after': {
                        bgcolor: activeTheme.palette.primary.main,
                        width: activeButton === item.name ? '80%' : '0%',
                        height: '2px',
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        transition: 'width 0.3s ease',
                      },
                      position: 'relative',
                    }}
                  >
                    {item.name}
                  </Button>
                );
              }

              if (item.type === 'dropdown') {
                const accessibleSubItems = item.items.filter((page) =>
                  hasAccessToPath(page.path)
                );

                if (accessibleSubItems.length === 0) return null;

                const isActive = activeButton === item.name;
                const isDropdownOpen = activeDropdown?.name === item.name;
                const anchorEl = isDropdownOpen ? activeDropdown.anchorEl : null;

                return (
                  <React.Fragment key={item.name}>
                    <Button
                      onClick={(e) => handleDropdownClick(item.name, e)}
                      aria-controls={isDropdownOpen ? `${item.name}-menu` : undefined}
                      aria-haspopup="true"
                      aria-expanded={isDropdownOpen ? 'true' : undefined}
                      className="nav-button"
                      startIcon={item.icon}
                      sx={{
                        color: activeTheme.palette.text.primary,
                        backgroundColor: 'transparent',
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                        '&::after': {
                          bgcolor: activeTheme.palette.primary.main,
                          width: isActive ? '80%' : '0%',
                          height: '2px',
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          transition: 'width 0.3s ease',
                        },
                        position: 'relative',
                      }}
                    >
                      {item.name}
                    </Button>

                    <Menu
                      id={`${item.name}-menu`}
                      anchorEl={anchorEl}
                      open={isDropdownOpen}
                      onClose={handleDropdownClose}
                      className="db-menu"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    >
                      {accessibleSubItems.map((page) => (
                        <MenuItem
                          key={page.name}
                          onClick={() =>
                            handleDropdownNavClick(item.name, page.name, page.path)
                          }
                          className="db-menu-item"
                        >
                          {page.name}
                        </MenuItem>
                      ))}
                    </Menu>
                  </React.Fragment>
                );
              }

              return null;
            })}
          </Box>

          {/* Right Section: Icons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 }, flex: '0 0 auto' }}>
            <Tooltip title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"} arrow>
              <IconButton
                size="small"
                onClick={handleThemeToggle}
                className="theme-toggle-button"
                sx={{
                  color: activeTheme.palette.text.primary,
                  padding: '6px',
                  '&:hover': {
                    backgroundColor: activeTheme.palette.action.hover,
                  },
                }}
              >
                {isDark ? <LightModeIcon sx={{ fontSize: '20px' }} /> : <DarkModeIcon sx={{ fontSize: '20px' }} />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Open Slack Channel" arrow>
              <IconButton
                size="small"
                onClick={handleSlackClick}
                className="slack-button"
                sx={{ 
                  color: activeTheme.palette.text.primary,
                  padding: '6px',
                  '&:hover': {
                    backgroundColor: activeTheme.palette.action.hover,
                  },
                }}
              >
                <img src={slackLogo} alt="Slack" style={{ width: '20px', height: '20px', filter: isDark ? 'invert(1)' : 'none' }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Copy URL for sharing" arrow>
              <IconButton
                size="small"
                onClick={handleCopyUrl}
                className="share-button"
                sx={{ 
                  color: activeTheme.palette.text.primary,
                  padding: '6px',
                  '&:hover': {
                    backgroundColor: activeTheme.palette.action.hover,
                  },
                }}
              >
                <ShareIcon sx={{ fontSize: '22px' }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Download Files Cart" arrow>
              <IconButton
                size="small"
                onClick={() => toggleDialog('download', true)}
                className="cart-button"
                sx={{ 
                  color: activeTheme.palette.text.primary,
                  padding: '6px',
                  '&:hover': {
                    backgroundColor: activeTheme.palette.action.hover,
                  },
                }}
              >
                <Badge badgeContent={badgeContent} color="error" sx={{ 
                  '& .MuiBadge-badge': {
                    fontSize: '0.65rem',
                    minWidth: '14px',
                    height: '14px',
                  }
                }}>
                  <ShoppingCartCheckoutIcon sx={{ fontSize: '22px' }} />
                </Badge>
              </IconButton>
            </Tooltip>

            <IconButton
              size="small"
              edge="end"
              aria-label="account of current user"
              onClick={(e) => setAnchorElUser(e.currentTarget)}
              className="account-button"
              sx={{ 
                color: activeTheme.palette.text.primary,
                padding: '6px',
                '&:hover': {
                  backgroundColor: activeTheme.palette.action.hover,
                },
              }}
            >
              <Avatar
                alt={getUserName()}
                className="avatar"
                sx={{
                  bgcolor: activeTheme.palette.action.selected,
                  color: activeTheme.palette.text.primary,
                  width: { xs: 28, md: 28 },
                  height: { xs: 28, md: 28 },
                  fontSize: { xs: '14px', md: '15px' },
                }}
              >
                {getUserName().charAt(0)}
              </Avatar>
            </IconButton>
          </Box>

          {/* User Menu */}
          <Menu
            className="user-menu"
            anchorEl={anchorElUser}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={Boolean(anchorElUser)}
            onClose={() => setAnchorElUser(null)}
          >
            <MenuItem onClick={handlePreferencesClick}>
              <Typography sx={{ textAlign: 'center' }}>Preferences</Typography>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <Typography sx={{ textAlign: 'center' }}>Logout</Typography>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer Menu */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleMobileMenuToggle}
        className="mobile-drawer"
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
            bgcolor: activeTheme.palette.background.paper,
            color: activeTheme.palette.text.primary,
          },
        }}
      >
        <Box sx={{ pt: 2, pb: 1 }}>
          <Box sx={{ px: 2, pb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <img src={gmLogo} alt="SDL" className="logo" style={{ height: '32px' }} />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              SDL
            </Typography>
          </Box>
          <Divider />
        </Box>
        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                navigate('/home');
                setMobileMenuOpen(false);
              }}
              sx={{
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                <HomeIcon />
              </ListItemIcon>
              <ListItemText primary="Home" />
            </ListItemButton>
          </ListItem>
          {navItems.map((item) => {
            if (item.type === 'page') {
              if (!hasAccessToPath(item.path)) return null;
              return (
                <ListItem key={item.name} disablePadding>
                  <ListItemButton
                    onClick={() => handleMobileNavClick(item.name, item.path)}
                    sx={{
                      backgroundColor: 'transparent',
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.name} />
                  </ListItemButton>
                </ListItem>
              );
            }

            if (item.type === 'dropdown') {
              const accessibleSubItems = item.items.filter((page) => hasAccessToPath(page.path));
              if (accessibleSubItems.length === 0) return null;

              return (
                <React.Fragment key={item.name}>
                  {accessibleSubItems.map((page) => {
                    const isActive = activeButton === item.name && location.pathname === page.path;
                    return (
                      <ListItem key={page.name} disablePadding sx={{ pl: 2 }}>
                        <ListItemButton
                          onClick={() => handleDropdownNavClick(item.name, page.name, page.path)}
                          sx={{
                            backgroundColor: 'transparent',
                            '&:hover': {
                              backgroundColor: 'transparent',
                            },
                          }}
                        >
                          <ListItemText primary={page.name} />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </React.Fragment>
              );
            }

            return null;
          })}
        </List>
        <Divider sx={{ mt: 'auto' }} />
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={handleSlackClick}>
              <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                <img src={slackLogo} alt="Slack" style={{ width: '24px', height: '24px', filter: isDark ? 'invert(1)' : 'none' }} />
              </ListItemIcon>
              <ListItemText primary="Slack Channel" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={handleCopyUrl}>
              <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                <ShareIcon />
              </ListItemIcon>
              <ListItemText primary="Share URL" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => { toggleDialog('download', true); setMobileMenuOpen(false); }}>
              <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                <Badge badgeContent={badgeContent} color="error">
                  <ShoppingCartCheckoutIcon />
                </Badge>
              </ListItemIcon>
              <ListItemText 
                primary="Download Cart" 
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => { handleThemeToggle(); setMobileMenuOpen(false); }}>
              <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                {isDark ? <LightModeIcon /> : <DarkModeIcon />}
              </ListItemIcon>
              <ListItemText primary={isDark ? "Light Mode" : "Dark Mode"} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={handlePreferencesClick}>
              <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                <TuneIcon />
              </ListItemIcon>
              <ListItemText primary="Preferences" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => { handleLogout(); setMobileMenuOpen(false); }}>
              <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>

      {/* Dialogs */}
      <DownloadCartDialog
        open={dialogs.download}
        onClose={() => toggleDialog('download', false)}
        activeTheme={activeTheme}
        apiCall={apiCall}
        badgeContent={badgeContent}
        setBadgeContent={setBadgeContent}
      />

      <ReleaseNotesDialog
        open={dialogs.releaseNotes}
        onClose={handleCloseReleaseNotes}
        activeTheme={activeTheme}
        releaseNotes={releaseNotes}
      />

    </Box>
  );
}

export default Navbar;